import axios from '@/lib/axios'
import { useCallback, useEffect } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { store, useAppDispatch } from '@/lib/redux/store'
import { useToasts } from './toasts'

export const useAuth = ({ middleware, redirectIfAuthenticated } = {}) =>
{
    const router = useRouter()
    const params = useParams()
    const dispatch = useAppDispatch();
    const { addToast } = useToasts();

    const register = async (props) =>
    {
        try
        {
            const response = await axios.post("api/register", props);
            if (response.status === 200)
            {
                addToast("success", "Customer registered successfully!");
                router.push("/login");
            } else
            {
                addToast("danger", response?.message || "Unexpected error occurred");
                router.push("/register");
            }
        } catch (error)
        {
            if (error.response?.status === 422)
            {
                addToast("danger", error.response.data.message || "Validation failed");
                return;
            }
            throw error;
        }
    };

    const login = async ({ setErrors, ...props }) =>
    {
        setErrors([])
        try
        {
            const response = await axios.post('/api/login', props)
            let userEntityRoles = response.data.data.user.user_entity_roles ?? [];
            // remove user role from list
            userEntityRoles = userEntityRoles.filter(uer => uer.role.config_key !== 'user');
            await dispatch({ type: 'app/setUser', payload: response.data.data.user });
            await dispatch({ type: 'app/setToken', payload: response.data.data.token });
            await dispatch({ type: 'app/setIsAuthenticated', payload: true });
            await dispatch({ type: 'app/setUserEntityRoles', payload: userEntityRoles });
            // update axios headers with token
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.token}`;
            if (userEntityRoles.length === 1)
            {
                const uer = userEntityRoles[0];
                await setRoleActingAs(uer);
            }
            router.push(`/verify-2fa`);
        } catch (error)
        {
            if (error.response.status !== 422) throw error
            setErrors(error.response.data.errors)
        }
    }

    const forgotPassword = async ({ setStatus, email }) =>
    {
        setStatus(null)
        return axios
            .post('/api/forgot-password', { email })
            .then(response => setStatus(response.data.status))
            .catch(error =>
            {
                throw error;
            })
    }

    const resetPassword = async ({ setErrors, setStatus, ...props }) =>
    {

        setErrors([])
        setStatus(null)
        axios
            .post('/api/reset-password', { token: params.token, ...props })
            .then(response =>
                router.push('/login'),
            )
            .catch(error =>
            {
                setErrors(error.response.data.errors)
                throw error;
            })
    }

    const resendEmailVerification = ({ setStatus }) =>
    {
        axios
            .post('/email/verification-notification')
            .then(response => setStatus(response.data.status))
    }

    const logout = useCallback(
        async (redirectUrl = '/login') =>
        {
            try
            {
                await axios.post('/api/logout');
            } catch (error)
            {
                console.error('Logout error:', error);
                addToast('danger', 'Failed to log out. Please try again.');
            } finally {
                await dispatch({ type: 'app/logOut' });

                delete axios.defaults.headers.common['Authorization'];
                // Clear all localStorage and sessionStorage
                localStorage.clear();
                sessionStorage.clear();

                if (redirectUrl) {
                    router.push(redirectUrl);
                }
            }
        },
        [dispatch, router, addToast]
    );

    const getUser = useCallback(async () =>
    {
        const response = await axios.get('/api/user')
        return response?.data?.data;
    }, []);

    const setRoleActingAs = useCallback(async (uer) =>
    {
        try
        {
            await dispatch({ type: 'app/setRoleActingAs', payload: uer });
            // router.push(`/verify-2fa`);
        } catch (error)
        {
            throw error;
        }
    }, [dispatch]);

    const refreshLoggedInUser = useCallback(async () =>
    {
        try
        {
            const user = await getUser();
            let userEntityRoles = user.user_entity_roles ?? [];
            userEntityRoles = userEntityRoles.filter(uer => uer.role.config_key !== 'user');
            await dispatch({ type: 'app/setUser', payload: user });
            await dispatch({ type: 'app/setUserEntityRoles', payload: userEntityRoles });
            if (userEntityRoles.length === 1)
            {
                const uer = userEntityRoles[0];
                await setRoleActingAs(uer);
            } else if (store.getState().app.roleActingAs)
            {
                // refresh roleActingAs from list
                const currentRole = store.getState().app.roleActingAs;
                const matchingRole = userEntityRoles.find(uer => uer.id === currentRole.id);
                if (matchingRole)
                {
                    await setRoleActingAs(matchingRole);
                } else
                {
                    // logout if no matching role found
                    await logout();
                }
            }
        } catch (error)
        {
            console.error('Error refreshing user:', error);
            addToast("danger", "Failed to refresh user data. Please try again.");
        }
    }, [getUser, dispatch, setRoleActingAs, logout, addToast]);
    // contact
    const contactUs = async ({ values, setErrors, setStatus }) =>
    {
        setErrors && setErrors({});
        setStatus && setStatus(null);
        try
        {
            const formData = new FormData();
            Object.entries(values).forEach(([key, val]) => formData.append(key, val));

            const response = await axios.post('/api/contact-us', formData);

        } catch (error)
        {
            if (error.response && error.response.status === 422)
            {
                setErrors && setErrors(error.response.data.errors);
            } else
            {
                addToast("danger", error.response?.data?.message || "Something went wrong while sending message.");
            }
        }
    };

    const isInstantPollsUser = (userEntityRole) =>
    {
        return userEntityRole && userEntityRole?.entity?.slug === 'admin';
    };

    const isInstantPollsAdmin = (userEntityRole) =>
    {
        return isInstantPollsUser(userEntityRole)
            && (userEntityRole?.role?.config_key === 'admin' || userEntityRole?.role?.config_key === 'owner');
    };

    const registerReseller = async (props) =>
    {
        try
        {
            const response = await axios.post("api/resellers", props);

            if (response.status === 200)
            {
                addToast("success", "Reseller registered successfully!");
                router.push("/login");
            } else
            {
                addToast("warning", response?.message || "Unexpected error occurred");
                router.push("/register");
            }
        } catch (error)
        {
            if (error.response?.status === 422)
            {
                addToast("warning", error.response.data.message || "Validation failed");
                return;
            }

            addToast(
                "danger",
                error.response?.data?.message || "An error occurred while registering reseller..."
            );
            throw error;
        }
    };
    // T&C and Privacy Policy document
    // Fetch all 4 legal documents in parallel
    const fetchLegalDocuments = useCallback(async () =>
    {
        try
        {
            const [
                resellerTermsRes,
                resellerPrivacyRes,
                customerTermsRes,
                customerPrivacyRes,
            ] = await Promise.all([
                axios.get('/api/legal-documents/reseller_terms_and_conditions'),
                axios.get('/api/legal-documents/reseller_privacy_policy'),
                axios.get('/api/legal-documents/customer_terms_and_conditions'),
                axios.get('/api/legal-documents/customer_privacy_policy'),
            ]);

            return {
                resellerTerms: resellerTermsRes.data,
                resellerPrivacy: resellerPrivacyRes.data,
                customerTerms: customerTermsRes.data,
                customerPrivacy: customerPrivacyRes.data,
            };
        } catch (error)
        {
            console.error('Error fetching legal documents:', error);
            addToast("danger", "Failed to load legal documents.");
            throw error;
        }
    }, [addToast]);

    return {
        register,
        login,
        forgotPassword,
        resetPassword,
        resendEmailVerification,
        logout,
        getUser,
        setRoleActingAs,
        contactUs,
        refreshLoggedInUser,
        isInstantPollsUser,
        isInstantPollsAdmin,
        registerReseller,
        fetchLegalDocuments
    }
}